import { Typography } from '@jouzen/ecom-components/v3/Typography';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { PriceInfoProps } from '@/app/components/PriceInfo';
import PriceInfo from '@/app/components/PriceInfo';

import Image from '../Image';
import RichText from '../RichText';

export interface AccessoryCardProps extends PriceInfoProps {
  readonly caption: MessageKey;
  readonly dataCy?: string;
  readonly href: string;
  readonly imageSrc: string;
  readonly title: MessageKey;
  readonly onClick?: () => void;
}

const AccessoryCard = ({
  dataCy,
  title,
  caption,
  imageSrc,
  href,
  discountedPrice,
  basePrice,
  priceText,
  currencyCode,
  showDecimalsForWholeDollars,
  onClick,
}: AccessoryCardProps) => {
  const router = useRouter();

  const handleClick = () => {
    onClick?.();
    router.push(href);
  };

  return (
    <article
      className="mb-6 cursor-pointer overflow-hidden rounded-3xl bg-transparent transition-all duration-700 ease-in-out focus-within:bg-white/75 focus-within:shadow-[0_16px_16px_rgba(0,0,0,0.05)] hover:bg-white/75 hover:shadow-[0_16px_16px_rgba(0,0,0,0.05)] md:mb-10 md:rounded-[40px]"
      data-cy="accessory-card"
      onClick={handleClick}
    >
      <div
        className="relative flex w-full flex-col items-center gap-2 px-10 py-2"
        data-cy={dataCy}
      >
        <span className="relative flex h-[156px] w-[181px] items-center justify-center lg:h-[260px] lg:w-[300px]">
          <Image
            className="object-contain"
            fill
            localizedAlt={title}
            sizes="(max-width: 1023px) 181px, 300px"
            src={imageSrc}
          />
        </span>
        <span>
          <Link className="block" href={href}>
            <Typography
              className="text-center text-xl text-sandstone-500 lg:text-2xl"
              Element="h3"
            >
              <RichText messageKey={title} />
            </Typography>
          </Link>
          <Typography className="text-center text-base text-gray-400 lg:text-xl">
            <RichText messageKey={caption} />
          </Typography>
        </span>
        <PriceInfo
          className="mb-6"
          priceClassName="text-base text-sandstone-500 lg:text-xl"
          discountedPrice={discountedPrice}
          basePrice={basePrice}
          currencyCode={currencyCode}
          priceText={priceText}
          showDecimalsForWholeDollars={showDecimalsForWholeDollars}
        />
      </div>
    </article>
  );
};

export default AccessoryCard;
